import type { CapDossierCover, CapNetworkSubmission } from '@dosfilos/domain';
import type { CapDossierPdfData, CapDossierPdfFinding } from './CapDossierPdfDocument';

// ─── De expediente a papel ───────────────────────────────────────────────────
//
// Una sola función, pura, entre el expediente y el PDF. Existe para que el papel
// no pueda decir algo distinto de la pantalla: recibe LA MISMA portada que
// renderiza la página (`buildCapDossierCover`) y el MISMO paquete, y se limita a
// resolver textos y ordenar.
//
// Los traductores entran por parámetro en vez de importarse. No es ceremonia:
// permite probar el mapeo sin i18n —que es donde viven los errores de este tipo
// de código, no en la redacción— y deja explícito que el idioma lo elige quien
// llama, que acá es el REVISOR.

export interface DossierPdfTranslators {
  /** Claves del namespace `cap`. */
  tc: (key: string, params?: Record<string, unknown>) => string;
  /** Claves del namespace `sis` (los nombres de sección A–K viven ahí). */
  ts: (key: string, params?: Record<string, unknown>) => string;
  formatDate: (value: string | undefined) => string;
}

export function buildCapDossierPdfData(input: {
  row: CapNetworkSubmission;
  cover: CapDossierCover;
  networkName: string;
  primaryColor?: string;
  generatedOn: Date;
  translators: DossierPdfTranslators;
}): CapDossierPdfData {
  const { row, cover, translators: tr } = input;
  const packet = row.packet;
  const digest = packet.compliance;

  const findings: CapDossierPdfFinding[] = cover.findings.map((f) => ({
    severity: f.severity,
    severityLabel: tr.tc(`dossier.severity.${f.severity}`),
    text: tr.tc(`dossier.findings.${f.code}`, {
      ...f.params,
      area: f.area ? tr.tc(`subjectAreas.${f.area}`) : '',
    }),
  }));

  return {
    brand: { networkName: input.networkName, primaryColor: input.primaryColor ?? '#334155' },
    stamp: {
      programName: row.programName,
      seminaryName: row.tmaiSiteAbbreviation
        ? `${row.seminaryName} · ${row.tmaiSiteAbbreviation}`
        : row.seminaryName,
      capCode: row.capCode,
      level: tr.tc(`levels.${row.level}`),
      round: row.round,
      version: row.proposalVersion,
      submittedOn: tr.formatDate(row.submittedAt),
      // La auditoría tiene su PROPIA fecha: puede ser anterior a la
      // presentación, y el comité necesita saber cuán vieja es la medición.
      ...(digest?.generatedAt ? { auditRunOn: tr.formatDate(digest.generatedAt) } : {}),
      generatedOn: tr.formatDate(input.generatedOn.toISOString()),
    },
    cover: {
      statusLabel: tr.tc(`compliance.status.${cover.overallStatus}`, {
        defaultValue: tr.tc(`dossier.status.${cover.overallStatus}`),
      }),
      ...(cover.stacked ? { stacked: tr.tc('dossier.stacked') } : {}),
      tcuLine: tr.tc('dossier.tcu', {
        actual: cover.totalActualTcu,
        min: cover.totalMinTcu,
      }),
      evidenceLine: tr.tc('dossier.evidenceLine', {
        courses: cover.evidence.courses,
        sections: cover.evidence.sections,
      }),
      findings,
      ...(findings.length === 0 ? { nothingToFlag: tr.tc('dossier.nothingToFlag') } : {}),
      reading: tr.tc(
        cover.counts.blocking === 0 ? 'dossier.readsAsMeeting' : 'dossier.readsAsShort',
      ),
    },
    areas: (digest?.subjectAreas ?? []).map((a) => ({
      name: tr.tc(`subjectAreas.${a.code}`),
      minTcu: a.minTcu,
      actualTcu: a.actualTcu,
      met: a.met,
    })),
    courses: (packet.ccdAnnex ?? []).map((c) => ({
      code: c.courseCode ?? '',
      name: c.courseName,
      area: c.ccd?.capSubjectArea ? tr.tc(`subjectAreas.${c.ccd.capSubjectArea}`) : '',
      // El CCD declara TCU por nivel: se imprime el del nivel auditado, que es
      // el que la auditoría contó.
      tcu: String(c.ccd?.tcu?.[row.level] ?? ''),
    })),
    sections: packet.sections.map((s) => ({
      heading: tr.ts(`cap.proposal.sections.${s.key}`, { defaultValue: s.key }),
      ...(s.content ? { body: s.content } : {}),
    })),
    attestations: packet.attestations
      .filter((a) => a.met)
      .map((a) => tr.tc(`proposal.attestations.${a.key}`, { defaultValue: a.key })),
    // Lo que el revisor ya escribió, si la ronda está decidida. Va al final y
    // literal: es la voz de una persona, no un resumen del sistema.
    ...(row.decision?.observations ? { observations: row.decision.observations } : {}),
    labels: {
      title: tr.tc('dossier.pdf.title'),
      coverTitle: tr.tc('dossier.title'),
      auditTitle: tr.tc('dossier.pdf.auditTitle'),
      coursesTitle: tr.tc('dossier.pdf.coursesTitle'),
      sectionsTitle: tr.tc('dossier.pdf.sectionsTitle'),
      attestationsTitle: tr.tc('dossier.pdf.attestationsTitle'),
      observationsTitle: tr.tc('dossier.pdf.observationsTitle'),
      area: tr.tc('compliance.area'),
      minTcu: tr.tc('dossier.pdf.minTcu'),
      actualTcu: tr.tc('dossier.pdf.actualTcu'),
      state: tr.tc('dossier.pdf.state'),
      met: tr.tc('dossier.pdf.met'),
      short: tr.tc('dossier.pdf.short'),
      round: tr.tc('dossier.pdf.round'),
      version: tr.tc('dossier.pdf.version'),
      submittedOn: tr.tc('dossier.pdf.submittedOn'),
      auditRunOn: tr.tc('dossier.pdf.auditRunOn'),
      generatedOn: tr.tc('dossier.pdf.generatedOn'),
      footerDisclaimer: tr.tc('dossier.pdf.footer'),
    },
  };
}
